import { type Dispatch, type SetStateAction, useState } from "react"

import eq from "lodash-es/eq"
import filter from "lodash-es/filter"
import forEach from "lodash-es/forEach"
import fromPairs from "lodash-es/fromPairs"
import last from "lodash-es/last"
import map from "lodash-es/map"
import mapValues from "lodash-es/mapValues"
import sortBy from "lodash-es/sortBy"
import throttle from "lodash-es/throttle"
import memoizeOne, { type MemoizedFn } from "memoize-one"
import { useMount } from "react-use"

import { THEME } from "@/3514/theme"
import { seconds } from "@/3514/utils"
import type { TEmptyCallback } from "@/shared/types/functions"

const breakpoints: { xs: number; sm: number; md: number; lg: number; xl: number; xxl: number } = {
    xs: 0,
    ...(fromPairs(
        map(THEME.BREAKPOINTS, (value: string, key: string): (string | number)[] => [key, parseInt(value, 10)])
    ) as {
        sm: number
        md: number
        lg: number
        xl: number
        xxl: number
    })
} as const

type TScreenBreakpoints = Record<keyof typeof breakpoints, boolean>

const _getMatches: MemoizedFn<(width: number) => TScreenBreakpoints> = memoizeOne(
    (width: number): TScreenBreakpoints =>
        mapValues(breakpoints, (_value: number, key: string): boolean =>
            eq(
                key,
                last<[string, number]>(
                    sortBy<[string, number]>(
                        filter<[string, number]>(
                            Object.entries(breakpoints),
                            ([, value]: [string, number]): boolean => width >= value
                        ),
                        ([, value]: [string, number]): number => value
                    )
                )?.[0]
            )
        ) as TScreenBreakpoints
)

function useScreenBreakpoints(): TScreenBreakpoints {
    const [matches, setMatches]: [TScreenBreakpoints, Dispatch<SetStateAction<TScreenBreakpoints>>] =
        useState<TScreenBreakpoints>(_getMatches(window.innerWidth))

    useMount((): TEmptyCallback => {
        const mediaQueries: { key: string; query: MediaQueryList }[] = map<
            [string, number],
            { key: string; query: MediaQueryList }
        >(Object.entries(breakpoints), ([key, value]: [string, number]): { key: string; query: MediaQueryList } => ({
            key,
            query: window.matchMedia(`(min-width: ${value}px)`)
        }))

        const handleChange: TEmptyCallback = throttle(
            (): void => setMatches(_getMatches(window.innerWidth)),
            seconds(0.1)
        )

        forEach<{ key: string; query: MediaQueryList }>(
            mediaQueries,
            ({ query }: { key: string; query: MediaQueryList }): void => query.addEventListener("change", handleChange)
        )

        return (): void =>
            void forEach<{ key: string; query: MediaQueryList }>(
                mediaQueries,
                ({ query }: { query: MediaQueryList }): void => query.removeEventListener("change", handleChange)
            )
    })

    return matches
}

export { useScreenBreakpoints }
export type { TScreenBreakpoints }
